/**
 * Browser push, from the page's side: register the service worker, ask for
 * permission, subscribe with the server's VAPID key, and hand the
 * subscription back to the server.
 *
 * Three things have to be true before an alert can reach a closed tab, and
 * the UI has to be able to say which one is missing:
 *
 *  1. a **secure context** — Web Push needs https (or localhost). Behind a
 *     plain-http reverse proxy `serviceWorker` is simply undefined;
 *  2. the browser's **permission**, which only a user gesture may request;
 *  3. a **VAPID key** on the server (`GET /api/push/key`).
 *
 * Without any of them the alert still lands in the log and still arrives
 * over the event stream — only the push to a closed tab is missing, which
 * is what {@link pushSupport} reports.
 */
import { postJson } from "./client";
import { paths } from "./client";
import type { AlertRow, PushSubscriptionInfo } from "./types";

export type PermissionState = "default" | "granted" | "denied" | "unsupported";

export interface PushSupport {
  /** The browser can do push at all (secure context, SW + Push API). */
  supported: boolean;
  permission: PermissionState;
  /** Why it cannot, in words the alerts page can show. */
  reason?: string;
}

export function pushSupport(): PushSupport {
  if (typeof window === "undefined") return { supported: false, permission: "unsupported" };
  if (!("Notification" in window)) {
    return { supported: false, permission: "unsupported", reason: "this browser has no notifications" };
  }
  const permission = Notification.permission as PermissionState;
  if (!window.isSecureContext) {
    return {
      supported: false,
      permission,
      reason: "push needs https (or localhost) — open the site through its TLS address",
    };
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { supported: false, permission, reason: "this browser has no Push API" };
  }
  return { supported: true, permission };
}

/** Ask the browser. Must be called from a click. */
export async function requestPermission(): Promise<PermissionState> {
  if (!("Notification" in window)) return "unsupported";
  return await Notification.requestPermission();
}

let registration: Promise<ServiceWorkerRegistration> | undefined;

/** Register (once per page load) the worker that receives pushes. */
export function serviceWorker(): Promise<ServiceWorkerRegistration> {
  registration ??= navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return registration;
}

/** `applicationServerKey` wants the raw bytes, not the base64url string. */
function decodeKey(base64url: string): Uint8Array {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function encodeKey(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A short name for the device list ("Firefox on Linux"). */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser =
    /Firefox\/(\d+)/.exec(ua)?.[0] ??
    /Edg\/(\d+)/.exec(ua)?.[0] ??
    /Chrome\/(\d+)/.exec(ua)?.[0] ??
    /Safari\/(\d+)/.exec(ua)?.[0] ??
    "browser";
  const platform = /Android/.test(ua)
    ? "Android"
    : /iPhone|iPad/.test(ua)
      ? "iOS"
      : /Linux/.test(ua)
        ? "Linux"
        : /Mac/.test(ua)
          ? "macOS"
          : /Win/.test(ua)
            ? "Windows"
            : "";
  return [browser.split("/")[0], platform].filter(Boolean).join(" on ");
}

/**
 * Subscribe this browser and tell the server. Safe to call on every load:
 * an existing subscription is reused, and re-posting it refreshes the row
 * rather than adding one.
 */
export async function subscribe(publicKey: string): Promise<PushSubscriptionInfo> {
  const reg = await serviceWorker();
  await navigator.serviceWorker.ready;
  let existing = await reg.pushManager.getSubscription();
  if (
    existing?.options.applicationServerKey &&
    encodeKey(existing.options.applicationServerKey) !== publicKey
  ) {
    await existing.unsubscribe();
    existing = null;
  }
  // A key change invalidates the old subscription: the push service would
  // accept it and the server could never sign for it again.
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey) as BufferSource,
    }));
  const body = {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: encodeKey(subscription.getKey("p256dh")),
      auth: encodeKey(subscription.getKey("auth")),
    },
    label: deviceLabel(),
  };
  const { data } = await postJson<PushSubscriptionInfo>(paths.pushSubscribe(), body);
  reg.active?.postMessage({ type: "flush-receipts" });
  return data;
}

/** Drop this browser's subscription, on both sides. */
export async function unsubscribe(): Promise<void> {
  const reg = await serviceWorker();
  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return;
  await postJson(paths.pushUnsubscribe(), { endpoint: subscription.endpoint }).catch(() => undefined);
  await subscription.unsubscribe();
}

/** Whether this browser already has a subscription registered. */
export async function isSubscribed(): Promise<boolean> {
  if (!pushSupport().supported) return false;
  const reg = await serviceWorker();
  return (await reg.pushManager.getSubscription()) !== null;
}

/**
 * Raise a notification from the page itself, for alerts that arrive over
 * the event stream while a tab is open. Goes through the service worker
 * when there is one so it looks identical to a pushed notification (and so
 * clicking it lands in the same place).
 */
export async function notifyLocally(alert: AlertRow, url: string) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    await serviceWorker();
    const reg = await navigator.serviceWorker.ready;
    reg.active?.postMessage({
      type: "notify-alert",
      payload: {
        id: alert.id,
        title: alert.title,
        body: alert.body,
        severity: alert.severity,
        at: alert.at,
        url,
      },
    });
  } catch {
    // Push remains queued on the server; do not bypass worker deduplication.
  }
}
